export type ShipTier = 'sloop' | 'brigantine' | 'galleon'

export interface ShipTierInfo {
  tier: ShipTier
  name: string
  description: string
  price: number // follis
  maxHealth: number
  speed: number // max surface movement units/sec (with optimal wind)
  thrust: number // forward thrust force
  dragCoeff: number // quadratic drag: thrust / speed^2
  turnRate: number // max radians/sec
  turnAccel: number // how fast turn rate responds (higher = snappier)
  // Wind profile: speed multipliers at [upwind, beamReach, downwind]
  windProfile: readonly [number, number, number]
  cannonsPerSide: number
  cannonDamage: number
  cannonCooldown: number // seconds between volleys
  cannonRange: number // units
  hullWidth: number
  hullLength: number
  hullHeight: number
  mastCount: number
}

export const SHIP_TIERS: Record<ShipTier, ShipTierInfo> = {
  sloop: {
    tier: 'sloop',
    name: 'Sloop',
    description: 'A small, fast vessel. Ideal for scouting and trade runs.',
    price: 500,
    maxHealth: 100,
    speed: 200,
    thrust: 130,       // reaches ~96% speed in ~3s
    dragCoeff: 0.00325, // = thrust / speed^2
    turnRate: 1.0,
    turnAccel: 5.0,     // nimble rudder response
    windProfile: [0.5, 1.0, 0.5] as const, // fore-and-aft: best at beam reach
    cannonsPerSide: 2,
    cannonDamage: 10,
    cannonCooldown: 3,
    cannonRange: 200,
    hullWidth: 3,
    hullLength: 12,
    hullHeight: 2.5,
    mastCount: 1,
  },
  brigantine: {
    tier: 'brigantine',
    name: 'Brigantine',
    description: 'A sturdy mid-size warship with balanced firepower and speed.',
    price: 2000,
    maxHealth: 250,
    speed: 150,
    thrust: 58,         // reaches ~96% speed in ~5s
    dragCoeff: 0.00258, // = thrust / speed^2
    turnRate: 0.7,
    turnAccel: 3.0,     // moderate rudder lag
    windProfile: [0.45, 1.0, 0.75] as const, // mixed rig: versatile
    cannonsPerSide: 4,
    cannonDamage: 15,
    cannonCooldown: 3.5,
    cannonRange: 300,
    hullWidth: 5,
    hullLength: 20,
    hullHeight: 4,
    mastCount: 2,
  },
  galleon: {
    tier: 'galleon',
    name: 'Galleon',
    description: 'A massive warship bristling with cannons. Slow but devastating.',
    price: 8000,
    maxHealth: 500,
    speed: 100,
    thrust: 24,         // reaches ~96% speed in ~8s
    dragCoeff: 0.0024,  // = thrust / speed^2
    turnRate: 0.4,
    turnAccel: 1.8,     // ponderous rudder
    windProfile: [0.15, 0.4, 1.0] as const, // square-rigged: great downwind only
    cannonsPerSide: 6,
    cannonDamage: 25,
    cannonCooldown: 4,
    cannonRange: 400,
    hullWidth: 8,
    hullLength: 32,
    hullHeight: 6,
    mastCount: 3,
  },
}

export const SHIP_TIER_ORDER: ShipTier[] = ['sloop', 'brigantine', 'galleon']
